var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import MuiSelect from '@mui/material/Select';
import { XCircleIcon } from '../icon';
import { IconButton } from '../icon-button';
import { InputAdornment } from '../input';
import { SelectMenu } from './select-menu';
import { styled } from '../styled';
import { Text } from '../text';
import clsx from '../utils/clsx';
import { getSelectUtilityClass, selectClasses, selectComponentName } from './select-classes';
import { renderSelectOptions } from './select-options';
import { useMergedClasses } from '../styles/classes';
var Root = styled(MuiSelect, { name: selectComponentName, slot: 'Root' })(function (_a) {
    var _b;
    var theme = _a.theme, ownerState = _a.ownerState;
    return (__assign(__assign({}, (ownerState.selectionDisabled && {
        color: theme.tokens.color_fg_default,
    })), (ownerState.adornedEnd && (_b = {
            paddingRight: 0
        },
        // MUI is doing some interesting stuff. This works around it: https://github.com/mui/material-ui/blob/master/packages/mui-material/src/NativeSelect/NativeSelectInput.js#L48-L49
        _b["&&& > .".concat(selectClasses.select)] = {
            paddingRight: ownerState.size === 'small' ? theme.spacing(8) : theme.spacing(9),
        },
        _b))));
});
var ClearInputAdornment = styled(InputAdornment, { name: selectComponentName, slot: 'ClearInputAdornment' })(function (_a) {
    var theme = _a.theme;
    return ({
        position: 'absolute',
        right: theme.typography.pxToRem(40),
    });
});
export var Select = React.forwardRef(function (props, ref) {
    var options = props.options, children = props.children, value = props.value, defaultValue = props.defaultValue, className = props.className, size = props.size, margin = props.margin, onClear = props.onClear, multiple = props.multiple, prefix = props.prefix, useCustomMenu = props.useCustomMenu, _a = props.MenuProps, MenuProps = _a === void 0 ? {} : _a, propClasses = props.classes, rootProps = __rest(props, ["options", "children", "value", "defaultValue", "className", "size", "margin", "onClear", "multiple", "prefix", "useCustomMenu", "MenuProps", "classes"]);
    var classes = useMergedClasses(selectClasses, getSelectUtilityClass, propClasses);
    var compareValue = value || defaultValue;
    var disabledOptionSelected = !!options && !multiple && !!options.find(function (option) { return option.value === compareValue && option.disabled; });
    var hasValue = multiple && Array.isArray(compareValue) ? !!compareValue.length : !disabledOptionSelected;
    var hasClearButton = !!onClear && hasValue;
    return (React.createElement(Root, __assign({ ref: ref, ownerState: { adornedEnd: hasClearButton, selectionDisabled: disabledOptionSelected, size: size }, value: value, defaultValue: defaultValue, multiple: multiple, size: size, className: clsx(classes.root, className), classes: {
            select: classes.select,
        }, endAdornment: hasClearButton && (React.createElement(ClearInputAdornment, { position: "end", className: classes.clearInputAdornment },
            React.createElement(IconButton, { edge: "end", size: size, onClick: onClear, className: classes.clearButton },
                React.createElement(XCircleIcon, null)))), startAdornment: !!prefix && (React.createElement(InputAdornment, { position: "start", className: classes.prefixInputAdornment },
            React.createElement(Text, { component: "span", variant: "body2", className: classes.prefixText }, prefix))), MenuProps: useCustomMenu
            ? __assign({ anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'right',
                }, transformOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                } }, MenuProps) : MenuProps }, rootProps), useCustomMenu ? (React.createElement(SelectMenu, { className: classes.selectMenu }, children)) : options ? (renderSelectOptions({ options: options, value: value || defaultValue })) : (children)));
});
